import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button, Form, Input, Spin } from "antd";
const { Password } = Input;
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CustomizeRequiredMark } from "~/components/common/custom-required-mark";
import { useNotification } from "~/hooks";
import { baseService } from "~/services/base";
import { inviteCodesService } from "~/services/invite-codes";
import { CheckCircleOutlined } from "@ant-design/icons";

type UserType = "shipper" | "collector" | "handler";

type RegisterFields = {
  inviteCode: string;
  userType: UserType;
  organizationName: string;
  slug: string;
  username: string;
  password: string;
  firstName: string;
  lastName: string;
  email: string;
};

const userTypeOptions: { value: UserType; label: string; description: string; features: string[] }[] = [
  {
    value: "shipper",
    label: "Shipper",
    description: "Shipping & logistics company",
    features: [
      "Job & service scheduling",
      "Fleet & crew management",
      "Inventory tracking & condition reports",
      "Client portal & invoicing",
      "Cross-company shuttle network",
      "Access to the freelancer network",
      "Digital forms & documents",
      "Email & SMS notifications",
    ],
  },
  {
    value: "collector",
    label: "Collectors, Dealers & Advisors",
    description: "Private collector, gallery, or art advisory",
    features: [
      "Request pickups & deliveries",
      "Collection management & cataloging",
      "Condition reporting & documentation",
      "Storage & location tracking",
      "Shared access for advisors & staff",
      "Access to freelancers & AHC trade network",
      "Digital forms & documents",
      "Email & SMS notifications",
    ],
  },
  {
    value: "handler",
    label: "Art Handler / Freelancer",
    description: "Independent art handler or freelance specialist",
    features: [
      "Personal job & assignment tracking",
      "Availability & scheduling",
      "Join crew rosters across companies",
      "Build your professional profile",
      "Get matched to open gigs",
      "5-level curriculum, resources & reference guides",
      "Digital forms & documents",
      "Email & SMS notifications",
    ],
  },
];

export const RegisterOrgView = () => {
  const navigate = useNavigate();
  const api = useNotification();
  const [searchParams] = useSearchParams();
  const inviteCodeFromUrl = searchParams.get("inviteCode") || "";

  const [userType, setUserType] = useState<UserType | null>(null);
  const [codeValidated, setCodeValidated] = useState(false);
  const [codeValidating, setCodeValidating] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    if (inviteCodeFromUrl) {
      form.setFieldsValue({ inviteCode: inviteCodeFromUrl });
      handleValidateCode(inviteCodeFromUrl);
    }
  }, [inviteCodeFromUrl]);

  const handleValidateCode = async (code: string) => {
    if (!code || code.length < 6) return;
    setCodeValidating(true);
    setCodeError(null);
    setCodeValidated(false);
    try {
      await inviteCodesService.validate(code);
      setCodeValidated(true);
    } catch (error: any) {
      setCodeError(
        error.response?.data?.message || "Invalid invite code"
      );
    } finally {
      setCodeValidating(false);
    }
  };

  const { mutate, isPending } = useMutation({
    mutationFn: async (values: RegisterFields) => {
      const { data } = await baseService.post("/organizations/register", values);
      return data;
    },
    onSuccess: () => {
      api.success({
        message: "Organization created",
        description: "You can now log in with your credentials.",
      });
      navigate("/login");
    },
    onError: (error: any) => {
      api.error({
        message: "Registration failed",
        description: error.response?.data?.message || "Something went wrong",
      });
    },
  });

  const onFinish = (values: RegisterFields) => {
    mutate({ ...values, userType: userType! });
  };

  const labelStyle = "text-xs font-bold uppercase tracking-wide text-[#1a1a2e]";
  const inputStyle =
    "border border-[#e5e5e5] rounded px-3 py-2 font-bold text-sm uppercase bg-white text-[#1a1a2e] placeholder:text-[#999] focus:border-[#1a1a2e] focus:ring-0";

  const selectedType = userTypeOptions.find((o) => o.value === userType);

  return (
    <main className="flex-1 flex items-center justify-center px-4 py-12 bg-white">
      <div className="w-full max-w-6xl mx-auto flex flex-col lg:flex-row gap-12 lg:gap-16">
        {/* Left Column — Type Selector + Features */}
        <div className="w-full lg:w-[380px] flex-shrink-0">
          {/* Brand Name */}
          <div className="mb-8 flex items-center gap-3">
            <h1
              className="text-5xl sm:text-6xl text-[#1a1a2e]"
              style={{
                fontFamily: "'Oswald', sans-serif",
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
              }}
            >
              Register
            </h1>
          </div>

          <p
            className="text-sm text-[#999] mb-6"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Join the platform. Select your role to get started.
          </p>

          {/* User Type Selector */}
          <div className="flex flex-col gap-3">
            {userTypeOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => { setUserType(opt.value); form.resetFields(["organizationName", "slug"]); }}
                className={`w-full text-left border rounded p-4 transition-all ${
                  userType === opt.value
                    ? "border-[#1a1a2e] bg-[#1a1a2e] text-white"
                    : "border-[#d9d9d9] bg-[#f5f5f5] text-[#1a1a2e] hover:border-[#999]"
                }`}
              >
                <span
                  className="block text-sm font-bold uppercase tracking-wide"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  {opt.label}
                </span>
                <span
                  className={`block text-xs mt-1 ${userType === opt.value ? "text-white/70" : "text-[#999]"}`}
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  {opt.description}
                </span>
              </button>
            ))}
          </div>

          {/* Features List */}
          {selectedType && (
            <div className="mt-8">
              <span
                className="block text-xs font-bold uppercase tracking-widest text-[#999] mb-4"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                What you get
              </span>
              <ul className="flex flex-col gap-3">
                {selectedType.features.map((feature, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-sm text-[#1a1a2e]"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    <CheckCircleOutlined className="text-[#1a1a2e] mt-0.5 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>

              {(userType === "shipper" || userType === "collector") && (
                <div className="mt-6 pt-6 border-t border-[#e5e5e5]">
                  <p
                    className="text-xs text-[#999] leading-relaxed"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    Includes <span className="font-bold text-[#1a1a2e]">AskOz</span> — your AI shipping agent that organizes job data, flags what needs attention, and makes sure nothing gets missed.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Column — Registration Form */}
        {userType && (
        <div className="flex-1 min-w-0">
          <div className="border border-[#d9d9d9] bg-[#f5f5f5] rounded p-6 sm:p-8">
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              requiredMark={CustomizeRequiredMark}
            >
              {/* Section: Invite Code */}
              <div className="flex items-center mb-4">
                <div className="flex-1 border-t border-[#e5e5e5]" />
                <span
                  className="px-4 text-xs font-bold uppercase tracking-widest text-[#999]"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  Invite Code
                </span>
                <div className="flex-1 border-t border-[#e5e5e5]" />
              </div>

              <Form.Item
                name="inviteCode"
                label={<span className={labelStyle} style={{ fontFamily: "'Montserrat', sans-serif" }}>Invite Code</span>}
                rules={[{ required: true, message: "An invite code is required to register" }]}
                extra={
                  codeValidating ? (
                    <span className="text-xs text-[#999]"><Spin size="small" /> Validating...</span>
                  ) : codeValidated ? (
                    <span className="text-xs text-green-600"><CheckCircleOutlined /> Valid invite code</span>
                  ) : null
                }
                validateStatus={codeError ? "error" : codeValidated ? "success" : undefined}
                help={codeError || undefined}
                className="mb-6"
              >
                <Input
                  size="large"
                  placeholder="Enter your invite code"
                  className={inputStyle}
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                  onBlur={(e) => handleValidateCode(e.target.value)}
                  onChange={() => {
                    setCodeValidated(false);
                    setCodeError(null);
                  }}
                />
              </Form.Item>

              {!codeValidated && !inviteCodeFromUrl && (
                <p
                  className="text-xs text-[#999] mb-6"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  Registration is currently invite-only. Contact us to learn more.
                </p>
              )}

              {/* Section: Organization Info */}
              <div className="flex items-center mb-4 mt-2">
                <div className="flex-1 border-t border-[#e5e5e5]" />
                <span
                  className="px-4 text-xs font-bold uppercase tracking-widest text-[#999]"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  {userType === "handler" ? "Your Business" : userType === "collector" ? "Organization" : "Company Info"}
                </span>
                <div className="flex-1 border-t border-[#e5e5e5]" />
              </div>

              <Form.Item
                name="organizationName"
                label={
                  <span className={labelStyle} style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    {userType === "handler" ? "Business Name" : userType === "collector" ? "Gallery / Organization Name" : "Company Name"}
                  </span>
                }
                rules={[{ required: true, message: "Required" }]}
                extra={
                  userType === "handler" ? (
                    <span className="text-xs text-[#999]">Use your own name if you operate independently</span>
                  ) : null
                }
                className="mb-6"
              >
                <Input
                  size="large"
                  placeholder={
                    userType === "handler"
                      ? "e.g. Rad Art Hanging"
                      : userType === "collector"
                      ? "e.g. Onglo Art Advisory"
                      : "e.g. Brooklyn Art Movers"
                  }
                  className={inputStyle}
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                />
              </Form.Item>

              <Form.Item
                name="slug"
                label={<span className={labelStyle} style={{ fontFamily: "'Montserrat', sans-serif" }}>URL Slug</span>}
                rules={[
                  { required: true, message: "Required" },
                  { pattern: /^[a-z0-9-]+$/, message: "Lowercase letters, numbers, and hyphens only" },
                ]}
                extra={
                  <span className="text-xs text-[#999]">
                    {userType === "handler"
                      ? 'Used in URLs. e.g. "rad-art-hanging"'
                      : userType === "collector"
                      ? 'Used in URLs. e.g. "onglo-art-advisory"'
                      : 'Used in URLs. e.g. "brooklyn-art-movers"'}
                  </span>
                }
                className="mb-6"
              >
                <Input
                  size="large"
                  placeholder={
                    userType === "handler" ? "rad-art-hanging" : userType === "collector" ? "onglo-art-advisory" : "brooklyn-art-movers"
                  }
                  className={inputStyle}
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                />
              </Form.Item>

              {/* Section: Account */}
              <div className="flex items-center mb-4 mt-2">
                <div className="flex-1 border-t border-[#e5e5e5]" />
                <span
                  className="px-4 text-xs font-bold uppercase tracking-widest text-[#999]"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  {userType === "handler" ? "Your Account" : "Admin Account"}
                </span>
                <div className="flex-1 border-t border-[#e5e5e5]" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Form.Item
                  name="firstName"
                  label={<span className={labelStyle} style={{ fontFamily: "'Montserrat', sans-serif" }}>First Name</span>}
                  rules={[{ required: true, message: "Required" }]}
                  className="mb-6"
                >
                  <Input size="large" className={inputStyle} style={{ fontFamily: "'Montserrat', sans-serif" }} />
                </Form.Item>
                <Form.Item
                  name="lastName"
                  label={<span className={labelStyle} style={{ fontFamily: "'Montserrat', sans-serif" }}>Last Name</span>}
                  rules={[{ required: true, message: "Required" }]}
                  className="mb-6"
                >
                  <Input size="large" className={inputStyle} style={{ fontFamily: "'Montserrat', sans-serif" }} />
                </Form.Item>
              </div>

              <Form.Item
                name="email"
                label={<span className={labelStyle} style={{ fontFamily: "'Montserrat', sans-serif" }}>Email</span>}
                rules={[
                  { required: true, message: "Required" },
                  { type: "email", message: "Invalid email" },
                ]}
                className="mb-6"
              >
                <Input size="large" placeholder="you@yourcompany.com" className={inputStyle} style={{ fontFamily: "'Montserrat', sans-serif" }} />
              </Form.Item>

              <Form.Item
                name="username"
                label={<span className={labelStyle} style={{ fontFamily: "'Montserrat', sans-serif" }}>Username</span>}
                rules={[{ required: true, message: "Required" }]}
                className="mb-6"
              >
                <Input size="large" className={inputStyle} style={{ fontFamily: "'Montserrat', sans-serif" }} />
              </Form.Item>

              <Form.Item
                name="password"
                label={<span className={labelStyle} style={{ fontFamily: "'Montserrat', sans-serif" }}>Password</span>}
                rules={[
                  { required: true, message: "Required" },
                  { min: 8, message: "Minimum 8 characters" },
                ]}
                className="mb-6"
              >
                <Password size="large" className={`${inputStyle} [&_input]:font-bold [&_input]:text-sm [&_input]:bg-white [&_input]:text-[#1a1a2e] [&_input]:placeholder:text-[#999] [&_input]:normal-case`} style={{ fontFamily: "'Montserrat', sans-serif" }} />
              </Form.Item>

              <Button
                loading={isPending}
                htmlType="submit"
                size="large"
                className="w-full border border-[#1a1a2e] rounded bg-[#1a1a2e] text-white px-4 py-3 font-bold text-sm uppercase tracking-wide hover:opacity-90 transition-all"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                {isPending ? "Creating..." : userType === "handler" ? "Create Account" : "Create Organization"}
              </Button>

              <div
                className="text-center text-sm font-medium text-[#1a1a2e] pt-6 border-t border-[#e5e5e5] mt-6"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                Already have an account?{" "}
                <Link
                  to="/login"
                  className="font-bold uppercase tracking-wide text-[#1a1a2e] hover:opacity-70 transition-opacity border-b border-transparent hover:border-[#1a1a2e]"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  Sign In
                </Link>
              </div>
            </Form>
          </div>
        </div>
        )}
      </div>
    </main>
  );
};
